
import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { App_url } from "../utils/constants/static";

gsap.registerPlugin(ScrollTrigger);

const About: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const leftNumberRef = useRef<HTMLDivElement | null>(null);
  const rightNumberRef = useRef<HTMLDivElement | null>(null);
  const lingRef = useRef<HTMLImageElement | null>(null);
  const logoRef = useRef<HTMLImageElement | null>(null);
  const imagesRef = useRef<(HTMLImageElement | null)[]>([]);
  const bgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      if (!containerRef.current) return;

      // Initial state — images hidden, numbers centered
      gsap.set(imagesRef.current, {
        clipPath: "inset(0 50% 0 50%)",
        scale: 1,
        opacity: 0,
        stagger: 0.6,
        transformOrigin: "center center",
      });

      gsap.set(leftNumberRef.current, { scale: 0, xPercent: -50, top: "50%", left: "50%", position: "absolute" });
      gsap.set(rightNumberRef.current, { scale: 0, xPercent: 50, top: "50%", left: "50%", position: "absolute" });

      // backgrounsd image move upward
      gsap.to(bgRef.current, {
        yPercent: '-100',
        ease: "none",
        // duration: 1.5,
        scrollTrigger: {
          trigger: bgRef.current,
          start: 'top 500',
          end: 'bottom+=1200 top',
          scrub: true,
          markers: false,
        }
      })


      // Timeline for scroll animation
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "+=3500",
          pin: true,
          scrub: 1.5,
          markers: false,
          // anticipatePin: 1,
        },
      });

      // Numbers move outward
      tl.to(
        leftNumberRef.current,
        {
          scale: 1,
          x: "-600",
          ease: "power2.out",
          duration: 1.5,
        },
        0
      );
      tl.to(
        rightNumberRef.current,
        {
          scale: 1,
          x: "600",
          ease: "power2.out",
          duration: 1.5,
        },
        0
      );

      // ling image move slightly
      tl.to(
        lingRef.current,
        {
          x: "600",
          ease: "power2.out",
          duration: 2,
        },
        0
      );

      // logo image move slightly
      tl.to(
        logoRef.current,
        {
          x: "-500",
          ease: "power2.out",
          duration: 2,
        },
        0
      );

      // Images open one by one
      tl.fromTo(
        imagesRef.current,
        {
          clipPath: "inset(0 50% 0 50%)",
          opacity: 0,
          scale: 0,
        },
        {
          clipPath: "inset(0 0% 0 0%)",
          scale: 1,
          opacity: 1,
          ease: "power2.inOut",
          stagger: 0.5,
          duration: 2,
        },
        0.5
      );

      // Numbers count 1 → 4
      gsap.to(
        {},
        {
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: "+=3000",
            scrub: 1,
            onUpdate: (self) => {
              const progress = self.progress;
              const num = Math.min(4, Math.max(1, Math.floor(progress * 4)));
              const formatted = num.toString().padStart(2, "0");
              if (leftNumberRef.current) leftNumberRef.current.innerText = formatted;
              if (rightNumberRef.current) rightNumberRef.current.innerText = formatted;
            },
          },
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const aboutImages = [
    App_url.image.about_1,
    App_url.image.about_2,
    App_url.image.about_3,
    App_url.image.about_4
  ];

  return (
    <>
      <div
        id="about"
        ref={containerRef}
        className="relative min-h-screen overflow-hidden"

      >
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0" style={{ backgroundImage: `url(${App_url.image.about_bg})` }}></div>
        <div ref={bgRef} className="bg_img h-full absolute bg-cover bg-center inset-0" style={{ backgroundImage: `url(${App_url.image.about_bg_img})` }}></div>

        <div className="flex flex-col justify-center items-center text-center gap-6 pt-15 relative ">
          {/* Image section */}
          <div className=" w-full max-w-[800px] h-[400px] flex items-center justify-center">
            {/* Left number (starts centered) */}
            <div
              ref={leftNumberRef}
              className=" text-white font-bold z-20 flex items-center justify-center leading-none"
            >
              <span className="inline-block">01</span>
            </div>

            {/* Image stack */}
            <div className="relative w-[750px] h-[300px] flex items-center justify-center">
              {aboutImages.map((img, i) => (
                <img
                  key={i}
                  ref={(el) => {
                    imagesRef.current[i] = el;
                  }}
                  src={img}
                  alt={`About ${i + 1}`}
                  className="absolute w-full h-full object-cover"
                />
              ))}
            </div>
            {/* Right number (starts centered) */}
            <div
              ref={rightNumberRef}
              className=" flex items-center justify-center text-white font-bold z-20 leading-none"
            >
              <span className="inline-block">01</span>
            </div>
          </div>

          {/* Description */}
          <div className="relative flex flex-row items-center justify-center gap-8 w-full text-white px-4">
            <img
              ref={logoRef}
              src="/about_left.svg"
              alt="logo"
              // className="absolute left-1/9 transform -translate-x-1/2 bottom-[-100px] w-auto object-contain opacity-90"
              className="absolute left-[32%] top-14 object-contain"
            />
            <p className="uppercase text-[13px] leading-none text-center ">
              Occupy creates bespoke jwellery as symbols of personal power —<br />
              shaped through a private, ritual-like process and offered in rare,
              limited form. <br />
              Each piece exists to mark, claim, and embody the quiet force of its
              wearer.
            </p>

            <img
              ref={lingRef}
              src={App_url.image.ling}
              alt="Ling"
              // className="absolute left-20% transform -translate-x-1/2 bottom-[-100px] h-[140px] md:h-[180px] lg:h-[220px] w-auto object-contain opacity-90"
              className="absolute right-[42%] top-16 transform h-[160px] md:h-[180px] lg:h-[200px] object-contain opacity-90"
            />
          </div>
        </div>
      </div>


    </>
  );
};

export default About;
